import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePen, KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Confirm, Loading } from "@/components/common";
import { LabelBadge } from "@/components/badges";
import { CompareTable, RevisionEditor } from "@/components/RevisionEditor";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, REQ_KIND, REQ_STATUS, REV_STATE } from "@/lib/format";

const ST_TONE = { pending: "amber", approved: "green", rejected: "red", cancelled: "slate", expired: "slate" };

function RequestRow({ r, i, canDecide, mine, onDone }) {
  const [reason, setReason] = useState("");
  const call = async (path, body, msg) => {
    try { await api.post(`/revision-requests/${r.id}/${path}`, body); toast.success(msg); window.dispatchEvent(new Event("revision-queue-changed")); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-slate-200 rounded-sm p-2 text-xs space-y-1 bg-white" data-testid={`rev-request-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <LabelBadge text={REQ_KIND[r.kind]} tone="blue" />
        <LabelBadge text={r.status === "approved" && r.kind === "permission" && r.used ? "DISETUJUI · SUDAH DIPAKAI" : REQ_STATUS[r.status]} tone={ST_TONE[r.status]} testId={`rev-request-status-${i}`} />
        <span>oleh <b>{r.requested_by}</b> · {fmtDateTime(r.requested_at)}</span>
      </div>
      <div>Alasan: {r.reason}</div>
      {r.decided_by && <div className="text-slate-600">Diproses {r.decided_by} · {fmtDateTime(r.decided_at)}{r.decision_reason ? ` · ${r.decision_reason}` : ""}</div>}
      {r.status === "approved" && r.kind === "permission" && !r.used && <div className="text-emerald-700">Izin berlaku sampai {fmtDateTime(r.edit_until)} (satu kali simpan)</div>}
      {r.status === "pending" && canDecide && !mine && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Confirm title="Setujui pengajuan ini?" description={r.kind === "proposal" ? "Usulan langsung menjadi revisi baru." : "Pengaju mendapat izin edit selama 3 hari (satu kali simpan)."} onConfirm={() => call("approve", {}, "Pengajuan disetujui")} testId={`rev-approve-${i}`}
            trigger={<Button size="sm" className="h-10 sm:h-8 bg-emerald-700 hover:bg-emerald-800" data-testid={`rev-approve-btn-${i}`}>Setujui</Button>} />
          <Input placeholder="Alasan tolak (min. 5)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-10 sm:h-8 w-48" data-testid={`rev-reject-reason-${i}`} />
          <Confirm title="Tolak pengajuan ini?" description="Invoice tetap seperti semula." onConfirm={() => call("reject", { reason }, "Pengajuan ditolak")} testId={`rev-reject-${i}`}
            trigger={<Button size="sm" disabled={reason.trim().length < 5} className="h-10 sm:h-8 bg-rose-600 hover:bg-rose-700" data-testid={`rev-reject-btn-${i}`}>Tolak</Button>} />
        </div>
      )}
      {r.status === "pending" && mine && (
        <Confirm title="Batalkan pengajuan?" onConfirm={() => call("cancel", {}, "Pengajuan dibatalkan")} testId={`rev-cancel-req-${i}`}
          trigger={<Button size="sm" variant="outline" className="h-10 sm:h-8" data-testid={`rev-cancel-req-btn-${i}`}>Batalkan Pengajuan</Button>} />
      )}
      {r.kind === "proposal" && r.proposed_lines && r.status === "pending" && <ProposalCompare r={r} />}
    </div>
  );
}

const ProposalCompare = ({ r }) => <CompareTable a={{ label: "Saat ini", lines: r.current_lines || [], total: r.current_total || 0 }} b={{ label: "Usulan", lines: r.proposed_lines, total: r.proposed_total }} testId="proposal-compare" />;

export function RevisionPanel({ inv, onChanged }) {
  const { can, user } = useAuth();
  const [d, setD] = useState(null);
  const [mode, setMode] = useState(null);
  const load = useCallback(async () => {
    try { setD((await api.get(`/invoices/${inv.id}/revisions`)).data); } catch (e) { toast.error(errMsg(e)); }
  }, [inv.id]);
  useEffect(() => {
    load();
  }, [load, inv.revision]);
  if (!d) return <Loading />;
  const active = d.requests.some((r) => r.status === "pending" || (r.status === "approved" && r.kind === "permission" && !r.used));
  const canReq = can("invoice:revise_request");
  const done = (msg) => { toast.success(msg); setMode(null); load(); onChanged(); };
  const submit = async ({ lines, notes, reason }) => {
    try {
      if (mode === "permission") { await api.post(`/invoices/${inv.id}/revision-requests`, { kind: "permission", reason }); done("Permintaan izin edit dikirim"); }
      else if (mode === "proposal") { await api.post(`/invoices/${inv.id}/revision-requests`, { kind: "proposal", reason, lines, notes }); done("Usulan perubahan dikirim"); }
      else { await api.post(`/invoices/${inv.id}/revise`, { lines, notes, reason, request_id: mode === "grant" ? d.my_grant.id : null }); done("Revisi tersimpan"); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reqs = d.requests.map((r) => ({ ...r, current_lines: inv.lines, current_total: inv.total }));
  const TITLES = { permission: ["Minta Izin Edit", false, "Kirim Permintaan"], proposal: ["Usulkan Perubahan", true, "Kirim Usulan"], direct: ["Edit Langsung (Admin Utama / Supervisor)", true, "Simpan Revisi"], grant: ["Edit dengan Izin", false, "Simpan Revisi"] };
  return (
    <div className="space-y-2" data-testid="revision-panel">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-600">Revisi:</span>
        {inv.revision ? <LabelBadge text={`REVISI R${inv.revision}`} tone="violet" testId="rev-label" /> : <LabelBadge text="ASLI" />}
        {d.revision_state && <LabelBadge text={REV_STATE[d.revision_state]} tone="amber" testId="rev-state" />}
      </div>
      {!mode && (
        <div className="flex flex-wrap gap-2">
          {canReq && !active && <Button variant="outline" className="h-11 sm:h-9" onClick={() => setMode("permission")} data-testid="rev-request-permission-btn"><KeyRound className="h-4 w-4 mr-2" />Minta Izin Edit</Button>}
          {canReq && !active && <Button variant="outline" className="h-11 sm:h-9" onClick={() => setMode("proposal")} data-testid="rev-propose-btn"><FilePen className="h-4 w-4 mr-2" />Usulkan Perubahan</Button>}
          {d.my_grant && <Button className="h-11 sm:h-9 bg-emerald-700 hover:bg-emerald-800" onClick={() => setMode("grant")} data-testid="rev-edit-grant-btn">Edit dengan Izin (sampai {fmtDateTime(d.my_grant.edit_until)})</Button>}
          {d.can_approve && <Button variant="outline" className="h-11 sm:h-9 border-emerald-600 text-emerald-800" onClick={() => setMode("direct")} data-testid="rev-direct-btn"><ShieldCheck className="h-4 w-4 mr-2" />Edit Langsung</Button>}
        </div>
      )}
      {mode && <RevisionEditor inv={inv} title={TITLES[mode][0]} needReason={mode !== "grant" || false} needLines={mode !== "permission"} submitLabel={TITLES[mode][2]} onSubmit={submit} onCancel={() => setMode(null)} />}
      {reqs.length > 0 && <div className="space-y-1">{reqs.map((r, i) => <RequestRow key={r.id} r={r} i={i} canDecide={d.can_approve} mine={r.requested_by_id === user?.id} onDone={() => { load(); onChanged(); }} />)}</div>}
    </div>
  );
}
